import { useState, useEffect } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import { Briefcase, Grid3x3, UserPlus, Plus, X } from 'lucide-react';
import './JobArchitectureApp.css';
import JobArchitectureGrid from './components/JobArchitectureGrid';
import ErrorBoundary from './components/ErrorBoundary';
import PromptModal from './components/PromptModal';
import ConfirmModal from './components/ConfirmModal';
import type { Card, JobFunction, JobFunctionGroup, CareerTrack } from './types';
import { gridApi, cardsApi } from './services/api';
import type { JobFunction as BackendJobFunction, JobLevel as BackendJobLevel, JobCard, GridConfiguration, CareerTrack as BackendCareerTrack } from './types/api';
import {
  mapJobCardToCard,
  buildJobFunctionToFrontendMap,
  buildLevelToRowIndexMap,
  buildFrontendToJobFunctionMap,
  buildRowIndexToLevelMap,
  transformGridConfiguration,
  extractJobFunctionId,
  extractCardId,
} from './services/dataMapper';

type ModalType =
  | 'addJobTitle'
  | 'addSubJob'
  | 'addCareerTrack'
  | 'addJobFunctionGroup'
  | 'addJobFunctionToGroup'
  | 'addLevel'
  | 'deleteCard'
  | 'deleteCareerTrack'
  | 'deleteLevel'
  | 'deleteJobFunction'
  | 'deleteJobFunctionGroup'
  | null;

interface ModalState {
  type: ModalType;
  data?: any;
}

function JobArchitectureApp() {
  const [cards, setCards] = useState<Card[]>([]);
  const [jobFunctionGroups, setJobFunctionGroups] = useState<JobFunctionGroup[]>([]);
  const [levels, setLevels] = useState<string[]>([]);
  const [careerTracks, setCareerTracks] = useState<CareerTrack[]>([]);
  const [modalState, setModalState] = useState<ModalState>({ type: null });
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Store backend data for mapping between frontend and backend IDs
  const [backendFunctions, setBackendFunctions] = useState<BackendJobFunction[]>([]);
  const [backendLevels, setBackendLevels] = useState<BackendJobLevel[]>([]);
  const [backendTracks, setBackendTracks] = useState<BackendCareerTrack[]>([]);

  // Single grid configuration ID (app only supports one grid)
  const GRID_CONFIG_ID = 1;

  // Load data from API on mount
  useEffect(() => {
    loadGridData();
  }, []);

  const loadGridData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load grid configuration
      const gridResponse = await gridApi.getConfiguration();
      const gridConfig: GridConfiguration = gridResponse.data;

      // Store backend data for ID mapping
      setBackendFunctions(gridConfig.job_functions);
      setBackendLevels(gridConfig.job_levels);
      setBackendTracks(gridConfig.career_tracks);

      // Transform to frontend format
      const { jobFunctionGroups: frontendGroups, levels: frontendLevels, careerTracks: frontendTracks } = transformGridConfiguration(gridConfig);
      setJobFunctionGroups(frontendGroups);
      setLevels(frontendLevels);
      setCareerTracks(frontendTracks);

      // Load cards
      const cardsResponse = await cardsApi.getAll();
      const backendCards: JobCard[] = cardsResponse.data;

      // Build mapping tables for card conversion
      const functionIdMap = buildJobFunctionToFrontendMap(gridConfig.job_functions);
      const levelIdMap = buildLevelToRowIndexMap(gridConfig.job_levels);

      // Convert backend cards to frontend format
      const frontendCards = backendCards.map(card =>
        mapJobCardToCard(card, functionIdMap, levelIdMap)
      );
      setCards(frontendCards);

      setLoading(false);
    } catch (err) {
      console.error('Failed to load grid data:', err);
      setError('Failed to load grid data. Please refresh the page.');
      setLoading(false);
    }
  };


  // Helper to get all functions from all groups (flattened)
  const getAllFunctions = (): JobFunction[] => {
    return jobFunctionGroups.flatMap(group => group.functions);
  };

  // Find first empty cell in grid
  const findEmptyCell = (): { functionId: string; row: number } => {
    const rows = levels.length;
    const allFunctions = getAllFunctions();

    for (let row = 0; row < rows; row++) {
      for (const func of allFunctions) {
        const occupied = cards.some(
          (card) => card.functionId === func.id && card.rowIndex === row
        );
        if (!occupied) {
          return { functionId: func.id, row };
        }
      }
    }

    // If all cells are full, return first function as fallback
    const firstFunction = allFunctions[0];
    return { functionId: firstFunction?.id || 'func-1', row: 0 };
  };

  // Add a new job title card
  const handleAddJobTitle = () => {
    setModalState({ type: 'addJobTitle' });
  };

  const handleAddJobTitleConfirm = async (jobTitle: string) => {
    const { functionId, row } = findEmptyCell();

    // Extract backend IDs from frontend IDs
    const backendFunctionId = extractJobFunctionId(functionId);
    const rowIndexToLevelIdMap = buildRowIndexToLevelMap(backendLevels);
    const backendLevelId = rowIndexToLevelIdMap.get(row);

    if (!backendFunctionId || !backendLevelId) {
      console.error('Failed to map frontend IDs to backend IDs');
      return;
    }

    try {
      // Create card via API
      const response = await cardsApi.create({
        job_function_id: backendFunctionId,
        job_level_id: backendLevelId,
        title: jobTitle,
        sort_order: 0,
      });

      const backendCard: JobCard = response.data;

      // Convert to frontend format and add to state
      const functionIdMap = buildJobFunctionToFrontendMap(backendFunctions);
      const levelIdMap = buildLevelToRowIndexMap(backendLevels);
      const frontendCard = mapJobCardToCard(backendCard, functionIdMap, levelIdMap);

      setCards((prev) => [...prev, frontendCard]);
    } catch (err) {
      console.error('Failed to create card:', err);
      setError('Failed to create job title');
    }
  };

  // Add a sub-job to an existing card
  const handleAddSubJob = (cardId: string) => {
    setModalState({ type: 'addSubJob', data: { cardId } });
  };

  const handleAddSubJobConfirm = async (jobTitle: string, cardId: string) => {
    const card = cards.find(c => c.id === cardId);
    if (!card) return;

    const backendCardId = extractCardId(cardId);
    if (!backendCardId) {
      console.error('Invalid card ID');
      return;
    }

    try {
      const updatedSubJobs = [...(card.subJobs || []), jobTitle.trim()];

      // Update card via API
      await cardsApi.partialUpdate(backendCardId, {
        sub_jobs: updatedSubJobs,
      });

      // Update local state
      setCards((prev) =>
        prev.map((c) => {
          if (c.id === cardId) {
            return { ...c, subJobs: updatedSubJobs };
          }
          return c;
        })
      );
    } catch (err) {
      console.error('Failed to add subjob:', err);
      setError('Failed to add job');
    }
  };

  // Edit a subjob
  const handleEditSubJob = async (cardId: string, subJobIndex: number, newContent: string) => {
    const card = cards.find(c => c.id === cardId);
    if (!card || !card.subJobs) return;

    const backendCardId = extractCardId(cardId);
    if (!backendCardId) return;

    try {
      const updatedSubJobs = [...card.subJobs];
      updatedSubJobs[subJobIndex] = newContent;

      // Update via API
      await cardsApi.partialUpdate(backendCardId, {
        sub_jobs: updatedSubJobs,
      });

      // Update local state
      setCards((prev) =>
        prev.map((c) => {
          if (c.id === cardId) {
            return { ...c, subJobs: updatedSubJobs };
          }
          return c;
        })
      );
    } catch (err) {
      console.error('Failed to edit subjob:', err);
      setError('Failed to edit job');
    }
  };

  // Remove a subjob
  const handleRemoveSubJob = async (cardId: string, subJobIndex: number) => {
    const card = cards.find(c => c.id === cardId);
    if (!card || !card.subJobs) return;

    const backendCardId = extractCardId(cardId);
    if (!backendCardId) return;

    try {
      const updatedSubJobs = card.subJobs.filter((_, idx) => idx !== subJobIndex);

      // Update via API - send array (empty or populated)
      await cardsApi.partialUpdate(backendCardId, {
        sub_jobs: updatedSubJobs,
      });

      // Update local state
      setCards((prev) =>
        prev.map((c) => {
          if (c.id === cardId) {
            return { ...c, subJobs: updatedSubJobs.length > 0 ? updatedSubJobs : undefined };
          }
          return c;
        })
      );
    } catch (err) {
      console.error('Failed to remove subjob:', err);
      setError('Failed to remove job');
    }
  };

  // Remove a card
  const handleRemoveCard = async (cardId: string) => {
    // Check if card has subjobs
    const card = cards.find(c => c.id === cardId);
    if (card?.subJobs && card.subJobs.length > 0) {
      // If card has subjobs, promote first subjob to main and remove it from subjobs
      const [firstSubJob, ...remainingSubJobs] = card.subJobs;

      const backendCardId = extractCardId(cardId);
      if (!backendCardId) return;

      try {
        // Update via API - promote first subjob to main title
        await cardsApi.partialUpdate(backendCardId, {
          title: firstSubJob,
          sub_jobs: remainingSubJobs,
        });

        // Update local state
        setCards((prev) =>
          prev.map((c) => {
            if (c.id === cardId) {
              return {
                ...c,
                content: firstSubJob,
                subJobs: remainingSubJobs.length > 0 ? remainingSubJobs : undefined,
              };
            }
            return c;
          })
        );
      } catch (err) {
        console.error('Failed to update card:', err);
        setError('Failed to remove job title');
      }
    } else {
      // No subjobs, delete the entire card
      setModalState({ type: 'deleteCard', data: { cardId } });
    }
  };

  const handleRemoveCardConfirm = async (cardId: string) => {
    const backendCardId = extractCardId(cardId);
    if (!backendCardId) return;

    try {
      // Delete via API
      await cardsApi.delete(backendCardId);

      // Update local state
      setCards((prev) => prev.filter((card) => card.id !== cardId));
    } catch (err) {
      console.error('Failed to delete card:', err);
      setError('Failed to delete position');
    }
  };

  // Edit a card's content
  const handleEditCard = async (cardId: string, newContent: string) => {
    const backendCardId = extractCardId(cardId);
    if (!backendCardId) return;

    try {
      // Update via API
      await cardsApi.partialUpdate(backendCardId, {
        title: newContent,
      });

      // Update local state
      setCards((prev) =>
        prev.map((card) =>
          card.id === cardId ? { ...card, content: newContent } : card
        )
      );
    } catch (err) {
      console.error('Failed to edit card:', err);
      setError('Failed to edit job title');
    }
  };

  // Handle drag and drop
  const handleDragEnd = async (result: DropResult) => {
    const { source, destination } = result;

    console.log('[Drag] Start', { source: source.droppableId, dest: destination?.droppableId });

    if (!destination) {
      console.log('[Drag] No destination, aborting');
      return;
    }

    const [sourceFunctionId, sourceRowStr] = source.droppableId.split(':');
    const [destFunctionId, destRowStr] = destination.droppableId.split(':');
    const sourceRow = parseInt(sourceRowStr, 10);
    const destRow = parseInt(destRowStr, 10);

    if (sourceFunctionId === destFunctionId && sourceRow === destRow) {
      console.log('[Drag] Same position, aborting');
      return;
    }

    const cardsToUpdate: Array<{ cardId: string; backendCardId: number; newFunctionId: string; newRowIndex: number }> = [];

    setCards((prevCards) => {
      const movedCardIndex = prevCards.findIndex(
        (card) => card.functionId === sourceFunctionId && card.rowIndex === sourceRow
      );

      if (movedCardIndex === -1) {
        console.log('[Drag] Card not found at source position');
        return prevCards;
      }

      const movedCard = prevCards[movedCardIndex];
      console.log('[Drag] Moving card:', movedCard.id, movedCard.content);

      const destOccupied = prevCards.some(
        (card, idx) => idx !== movedCardIndex && card.functionId === destFunctionId && card.rowIndex === destRow
      );

      console.log('[Drag] Destination occupied:', destOccupied);

      const newCards = prevCards.map((card, index) => {
        const cleanCard = { ...card, isPreview: undefined };

        if (index === movedCardIndex) {
          return cleanCard;
        }

        if (
          destOccupied &&
          card.functionId === destFunctionId &&
          card.rowIndex >= destRow
        ) {
          const backendCardId = extractCardId(card.id);
          if (backendCardId) {
            console.log('[Drag] Shifting down:', card.id, card.content, 'from row', card.rowIndex, 'to', card.rowIndex + 1);
            cardsToUpdate.push({
              cardId: card.id,
              backendCardId,
              newFunctionId: destFunctionId,
              newRowIndex: card.rowIndex + 1,
            });
          }

          return {
            ...cleanCard,
            rowIndex: card.rowIndex + 1,
          };
        }

        return cleanCard;
      });

      newCards[movedCardIndex] = {
        ...movedCard,
        functionId: destFunctionId,
        rowIndex: destRow,
        isPreview: undefined,
      };

      const movedBackendCardId = extractCardId(movedCard.id);
      if (movedBackendCardId) {
        console.log('[Drag] Adding moved card to updates:', movedBackendCardId);
        cardsToUpdate.push({
          cardId: movedCard.id,
          backendCardId: movedBackendCardId,
          newFunctionId: destFunctionId,
          newRowIndex: destRow,
        });
      }

      console.log('[Drag] Total cards to update:', cardsToUpdate.length);
      return newCards;
    });

    console.log('[Drag] Cards to update array:', cardsToUpdate);

    try {
      const functionIdToBackendId = buildFrontendToJobFunctionMap(backendFunctions);
      const rowIndexToLevelId = buildRowIndexToLevelMap(backendLevels);

      for (const update of cardsToUpdate) {
        const backendFunctionId = functionIdToBackendId.get(update.newFunctionId);
        const backendLevelId = rowIndexToLevelId.get(update.newRowIndex);

        console.log('[Drag] Processing update:', {
          cardId: update.cardId,
          backendCardId: update.backendCardId,
          frontendFunctionId: update.newFunctionId,
          backendFunctionId,
          frontendRowIndex: update.newRowIndex,
          backendLevelId,
        });

        if (backendFunctionId && backendLevelId) {
          await cardsApi.updatePosition(update.backendCardId, {
            job_function_id: backendFunctionId,
            job_level_id: backendLevelId,
          });
        } else {
          console.warn('[Drag] Skipping update - missing backend IDs');
        }
      }
      console.log('[Drag] All updates complete');
    } catch (err) {
      console.error('Failed to update card positions:', err);
      setError('Failed to move card. Please refresh the page.');
      loadGridData();
    }
  };

  // Add a new career track
  const handleAddCareerTrack = () => {
    setModalState({ type: 'addCareerTrack' });
  };

  const handleAddCareerTrackConfirm = async (trackName: string) => {
    try {
      // Create career track via API
      await gridApi.createCareerTrack({
        grid_config_id: GRID_CONFIG_ID,
        name: trackName,
        sort_order: careerTracks.length,
      });

      // Reload grid data to stay in sync
      await loadGridData();
    } catch (err) {
      console.error('Failed to create career track:', err);
      setError('Failed to create career track');
    }
  };

  // Delete career track by index
  const handleDeleteCareerTrackByIndex = (trackIndex: number) => {
    setModalState({
      type: 'deleteCareerTrack',
      data: { trackIndex },
    });
  };

  const handleDeleteCareerTrackConfirm = async (trackIndex: number) => {
    // Get the backend track ID using the sorted backend tracks
    const sortedBackendTracks = [...backendTracks].sort((a, b) => a.sort_order - b.sort_order);
    const backendTrack = sortedBackendTracks[trackIndex];

    if (!backendTrack) {
      console.error('Track not found at index', trackIndex);
      return;
    }

    try {
      // Delete via API - backend will cascade delete levels and cards
      await gridApi.deleteCareerTrack(backendTrack.id);

      // Reload grid data to stay in sync with backend
      await loadGridData();
    } catch (err) {
      console.error('Failed to delete career track:', err);
      setError('Failed to delete career track');
    }
  };

  // Add a level to a specific career track
  const handleAddLevelToCareerTrack = (trackIndex: number) => {
    setModalState({ type: 'addLevel', data: { trackIndex } });
  };

  const handleAddLevelConfirm = async (levelName: string, trackIndex: number) => {
    // Get the backend track ID using the sorted backend tracks
    const sortedBackendTracks = [...backendTracks].sort((a, b) => a.sort_order - b.sort_order);
    const backendTrack = sortedBackendTracks[trackIndex];

    if (!backendTrack) {
      console.error('Track not found at index', trackIndex);
      return;
    }

    try {
      // Create level via API - backend will auto-calculate sort_order
      await gridApi.createJobLevel({
        grid_config_id: GRID_CONFIG_ID,
        career_track_id: backendTrack.id,
        name: levelName.trim(),
      });

      // Reload grid data to stay in sync
      await loadGridData();
    } catch (err) {
      console.error('Failed to create level:', err);
      setError('Failed to create level');
    }
  };

  // Delete level by index
  const handleDeleteLevelByIndex = (levelIndex: number) => {
    setModalState({ type: 'deleteLevel', data: { levelIndex } });
  };

  const handleDeleteLevelConfirm = async (levelIndex: number) => {
    // Build the frontend levels array to map index to backend level
    const sortedTracks = [...backendTracks].sort((a, b) => a.sort_order - b.sort_order);
    const levelMapping: (number | null)[] = [];

    for (const track of sortedTracks) {
      const trackLevels = backendLevels
        .filter(level => level.career_track_id === track.id)
        .sort((a, b) => a.sort_order - b.sort_order);

      if (trackLevels.length === 0) {
        // Empty track - placeholder (no backend level)
        levelMapping.push(null);
      } else {
        // Add backend level IDs
        trackLevels.forEach(level => levelMapping.push(level.id));
      }
    }

    const backendLevelId = levelMapping[levelIndex];
    if (!backendLevelId) {
      console.error('Cannot delete placeholder level at index', levelIndex);
      return;
    }

    try {
      // Delete via API - backend will cascade delete cards in this level
      await gridApi.deleteJobLevel(backendLevelId);

      // Reload grid data to stay in sync
      await loadGridData();
    } catch (err) {
      console.error('Failed to delete level:', err);
      setError('Failed to delete level');
    }
  };

  // Add a new job function group
  const handleAddJobFunctionGroup = () => {
    setModalState({ type: 'addJobFunctionGroup' });
  };

  const handleAddJobFunctionGroupConfirm = async (groupName: string) => {
    try {
      // Create group via API
      await gridApi.createJobFunctionGroup({
        grid_config_id: GRID_CONFIG_ID,
        name: groupName.trim(),
        sort_order: jobFunctionGroups.length,
      });

      // Reload grid data to stay in sync
      await loadGridData();
    } catch (err) {
      console.error('Failed to create job function group:', err);
      setError('Failed to create job function group');
    }
  };

  // Add a new job function to a group
  const handleAddJobFunctionToGroup = (groupId: string) => {
    setModalState({ type: 'addJobFunctionToGroup', data: { groupId } });
  };

  const handleAddJobFunctionToGroupConfirm = async (functionName: string, groupId: string) => {
    // Extract backend group ID from frontend ID (format: "group-123")
    const backendGroupId = parseInt(groupId.replace('group-', ''), 10);
    if (isNaN(backendGroupId)) {
      console.error('Invalid group ID:', groupId);
      return;
    }

    // Count existing functions to determine sort_order
    const group = jobFunctionGroups.find(g => g.id === groupId);
    const sortOrder = group ? group.functions.length : 0;

    try {
      // Create function via API
      await gridApi.createJobFunction({
        grid_config_id: GRID_CONFIG_ID,
        job_function_group_id: backendGroupId,
        name: functionName.trim(),
        sort_order: sortOrder,
      });

      // Reload grid data to stay in sync
      await loadGridData();
    } catch (err) {
      console.error('Failed to create job function:', err);
      setError('Failed to create job function');
    }
  };

  // Delete a job function
  const handleDeleteJobFunction = (functionId: string) => {
    setModalState({ type: 'deleteJobFunction', data: { functionId } });
  };

  const handleDeleteJobFunctionConfirm = async (functionId: string) => {
    const backendFunctionId = extractJobFunctionId(functionId);
    if (!backendFunctionId) {
      console.error('Invalid function ID:', functionId);
      return;
    }

    try {
      // Delete via API - backend will cascade delete cards
      await gridApi.deleteJobFunction(backendFunctionId);

      // Reload grid data to stay in sync
      await loadGridData();
    } catch (err) {
      console.error('Failed to delete job function:', err);
      setError('Failed to delete job function');
    }
  };

  // Delete a job function group
  const handleDeleteJobFunctionGroup = (groupId: string) => {
    setModalState({ type: 'deleteJobFunctionGroup', data: { groupId } });
  };

  const handleDeleteJobFunctionGroupConfirm = async (groupId: string) => {
    // Extract backend group ID from frontend ID (format: "group-123")
    const backendGroupId = parseInt(groupId.replace('group-', ''), 10);
    if (isNaN(backendGroupId)) {
      console.error('Invalid group ID:', groupId);
      return;
    }

    try {
      // Delete via API - backend will cascade delete functions and cards
      await gridApi.deleteJobFunctionGroup(backendGroupId);

      // Reload grid data to stay in sync
      await loadGridData();
    } catch (err) {
      console.error('Failed to delete job function group:', err);
      setError('Failed to delete job function group');
    }
  };

  return (
    <ErrorBoundary>
      <div className="job-architecture-app">
        <header className="app-header">
          <h1>Job Architecture Framework</h1>
        </header>

        {/* Loading State */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner">Loading...</div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="error-close">×</button>
          </div>
        )}

        <div className="app-content">
          {/* Placeholder for static images (to be added) */}
          <div className="image-placeholder image-top">
            {/* Static image will go here */}
          </div>

          {/* Action buttons */}
          <div className="grid-action-buttons">
            <button onClick={handleAddCareerTrack} className="btn-with-icon btn-structure">
              <Briefcase size={18} />
              <span>Add Career Track</span>
            </button>
            <button onClick={handleAddJobFunctionGroup} className="btn-with-icon btn-structure">
              <Grid3x3 size={18} />
              <span>Add Job Function Group</span>
            </button>
            <button onClick={handleAddJobTitle} className="btn-with-icon btn-content">
              <UserPlus size={18} />
              <span>Add Job Title</span>
            </button>
          </div>

          <div className="grid-wrapper-with-static">
            {/* Static column: Career Track */}
            <div className="static-column career-track-column">
              <div className="static-column-header">Career Track</div>
              {careerTracks.map((track, idx) => {
                // Calculate starting row index for this track
                let startRow = 0;
                for (let i = 0; i < idx; i++) {
                  startRow += careerTracks[i].rowspan;
                }
                const endRow = startRow + track.rowspan;

                // Calculate total height based on rowHeights (30px per job)
                let totalHeight = 0;
                for (let i = startRow; i < endRow && i < rowHeights.length; i++) {
                  totalHeight += (rowHeights[i] || 1) * 30;
                }
                // Fallback if rowHeights not yet calculated
                if (totalHeight === 0) {
                  totalHeight = track.rowspan * 30;
                }

                return (
                  <div
                    key={`track-${idx}`}
                    className="static-cell career-track-cell"
                    style={{ height: `${totalHeight}px` }}
                  >
                    <button
                      className="cell-delete-button"
                      onClick={() => handleDeleteCareerTrackByIndex(idx)}
                      title="Delete this career track"
                      aria-label={`Delete ${track.label}`}
                    >
                      <X size={14} />
                    </button>
                    <span className="cell-label">{track.label}</span>
                    <button
                      className="cell-add-button"
                      onClick={() => handleAddLevelToCareerTrack(idx)}
                      title="Add level to this career track"
                      aria-label={`Add level to ${track.label}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Static column: Levels */}
            <div className="static-column levels-column">
              <div className="static-column-header">Levels</div>
              {levels.map((level, idx) => {
                const heightInPx = rowHeights[idx] ? rowHeights[idx] * 30 : 30;
                const isPlaceholder = level.startsWith('(Empty - add level');
                return (
                  <div
                    key={`level-${idx}`}
                    className={`static-cell level-cell ${isPlaceholder ? 'empty-level-placeholder' : ''}`}
                    style={{ height: `${heightInPx}px` }}
                  >
                    {!isPlaceholder && (
                      <button
                        className="cell-delete-button"
                        onClick={() => handleDeleteLevelByIndex(idx)}
                        title="Delete this level"
                        aria-label={`Delete ${level}`}
                      >
                        <X size={14} />
                      </button>
                    )}
                    <span className="cell-label">{level}</span>
                  </div>
                );
              })}
            </div>

            {/* Main grid with DnD */}
            <div className="main-grid-container">
              <DragDropContext onDragEnd={handleDragEnd}>
                <JobArchitectureGrid
                  jobFunctionGroups={jobFunctionGroups}
                  levels={levels}
                  cards={cards}
                  onRemoveCard={handleRemoveCard}
                  onEditCard={handleEditCard}
                  onEditSubJob={handleEditSubJob}
                  onRemoveSubJob={handleRemoveSubJob}
                  onAddSubJob={handleAddSubJob}
                  onRowHeightsCalculated={setRowHeights}
                  onAddFunctionToGroup={handleAddJobFunctionToGroup}
                  onDeleteFunction={handleDeleteJobFunction}
                  onDeleteGroup={handleDeleteJobFunctionGroup}
                />
              </DragDropContext>
            </div>
          </div>

          {/* Placeholder for static images (to be added) */}
          <div className="image-placeholder image-bottom">
            {/* Static image will go here */}
          </div>
        </div>

        {/* Modals */}
        <PromptModal
          isOpen={modalState.type === 'addJobTitle'}
          onClose={() => setModalState({ type: null })}
          onConfirm={handleAddJobTitleConfirm}
          title="Add Job Title"
          label="Job Title"
          placeholder="Enter job title"
        />

        <PromptModal
          isOpen={modalState.type === 'addCareerTrack'}
          onClose={() => setModalState({ type: null })}
          onConfirm={handleAddCareerTrackConfirm}
          title="Add Career Track"
          label="Career Track Name"
          placeholder="Enter career track name"
        />

        <PromptModal
          isOpen={modalState.type === 'addJobFunctionGroup'}
          onClose={() => setModalState({ type: null })}
          onConfirm={handleAddJobFunctionGroupConfirm}
          title="Add Job Function Group"
          label="Group Name"
          placeholder="Enter group name"
        />

        <PromptModal
          isOpen={modalState.type === 'addJobFunctionToGroup'}
          onClose={() => setModalState({ type: null })}
          onConfirm={(functionName) => {
            if (modalState.data?.groupId) {
              handleAddJobFunctionToGroupConfirm(functionName, modalState.data.groupId);
            }
          }}
          title="Add Job Function"
          label="Job Function Name"
          placeholder="Enter job function name"
        />

        <PromptModal
          isOpen={modalState.type === 'addLevel'}
          onClose={() => setModalState({ type: null })}
          onConfirm={(levelName) => {
            if (modalState.data?.trackIndex !== undefined) {
              handleAddLevelConfirm(levelName, modalState.data.trackIndex);
            }
          }}
          title="Add Level"
          label="Level Name"
          placeholder="Enter level name"
        />

        <PromptModal
          isOpen={modalState.type === 'addSubJob'}
          onClose={() => setModalState({ type: null })}
          onConfirm={(jobTitle) => {
            if (modalState.data?.cardId) {
              handleAddSubJobConfirm(jobTitle, modalState.data.cardId);
            }
          }}
          title="Add Job"
          label="Job Title"
          placeholder="Enter job title"
        />

        <ConfirmModal
          isOpen={modalState.type === 'deleteCard'}
          onClose={() => setModalState({ type: null })}
          onConfirm={() => {
            if (modalState.data?.cardId) {
              handleRemoveCardConfirm(modalState.data.cardId);
            }
          }}
          title="Delete Position"
          message="Are you sure you want to delete this position?"
          confirmText="Delete"
          isDangerous={true}
        />

        <ConfirmModal
          isOpen={modalState.type === 'deleteCareerTrack'}
          onClose={() => setModalState({ type: null })}
          onConfirm={() => {
            if (modalState.data?.trackIndex !== undefined) {
              handleDeleteCareerTrackConfirm(modalState.data.trackIndex);
            }
          }}
          title="Delete Career Track"
          message={
            modalState.data?.trackIndex !== undefined
              ? `Delete career track "${careerTracks[modalState.data.trackIndex]?.label}"? This will remove all cards in this track.`
              : 'Delete this career track?'
          }
          confirmText="Delete"
          isDangerous={true}
        />

        <ConfirmModal
          isOpen={modalState.type === 'deleteLevel'}
          onClose={() => setModalState({ type: null })}
          onConfirm={() => {
            if (modalState.data?.levelIndex !== undefined) {
              handleDeleteLevelConfirm(modalState.data.levelIndex);
            }
          }}
          title="Delete Level"
          message={
            modalState.data?.levelIndex !== undefined
              ? `Delete level "${levels[modalState.data.levelIndex]}"? This will remove all cards in this level.`
              : 'Delete this level?'
          }
          confirmText="Delete"
          isDangerous={true}
        />

        <ConfirmModal
          isOpen={modalState.type === 'deleteJobFunction'}
          onClose={() => setModalState({ type: null })}
          onConfirm={() => {
            if (modalState.data?.functionId) {
              handleDeleteJobFunctionConfirm(modalState.data.functionId);
            }
          }}
          title="Delete Job Function"
          message={
            modalState.data?.functionId
              ? (() => {
                  const allFunctions = getAllFunctions();
                  const func = allFunctions.find(f => f.id === modalState.data.functionId);
                  const cardCount = cards.filter(c => c.functionId === modalState.data.functionId).length;
                  return `Delete job function "${func?.label}"? This will remove ${cardCount} card${cardCount !== 1 ? 's' : ''}.`;
                })()
              : 'Delete this job function?'
          }
          confirmText="Delete"
          isDangerous={true}
        />

        <ConfirmModal
          isOpen={modalState.type === 'deleteJobFunctionGroup'}
          onClose={() => setModalState({ type: null })}
          onConfirm={() => {
            if (modalState.data?.groupId) {
              handleDeleteJobFunctionGroupConfirm(modalState.data.groupId);
            }
          }}
          title="Delete Job Function Group"
          message={
            modalState.data?.groupId
              ? (() => {
                  const group = jobFunctionGroups.find(g => g.id === modalState.data.groupId);
                  const functionCount = group?.functions.length || 0;
                  const functionIds = group?.functions.map(f => f.id) || [];
                  const cardCount = cards.filter(c => functionIds.includes(c.functionId)).length;
                  return `Delete group "${group?.label}"? This will remove ${functionCount} job function${functionCount !== 1 ? 's' : ''} and ${cardCount} card${cardCount !== 1 ? 's' : ''}.`;
                })()
              : 'Delete this job function group?'
          }
          confirmText="Delete"
          isDangerous={true}
        />
      </div>
    </ErrorBoundary>
  );
}

export default JobArchitectureApp;
